import {FC, Fragment} from 'react'
import {isNotEmpty, onTSP, URL} from '../../../../../../_metronic/helpers'
import {Invoice, InvoicePaymentTypeItems, InvoiceTypeList} from '../core/_models'
import {QRCode} from 'react-qrcode-logo'
import './InvoicePrintModal.css'
import {getPaymentTaxValidationError} from '../core/paymentTaxValidation'
import {article9UnixMillisecondsToDateTime} from '../core/invoiceArticle9'
import {
  isSandboxInvoice,
  isTaxIdFinal,
  PROVISIONAL_TAX_ID_NOTICE,
} from '../core/invoiceStatusHelpers'
import {qrLogoImage} from './classic/classicImages'

type Props = {
  invoice: Invoice
  /** The description and the template's text as plain lines (the printed invoice's). */
  noteLines?: string[]
  /** The type and pattern as the printed sheets name them («نوع ۱ …», «الگوی ۱ (فروش)»). */
  title?: string
  subtitle?: string
}

const InvoicePrintPOS: FC<Props> = ({invoice, noteLines, title, subtitle}) => {
  const isArticle9 = invoice.insr === 1
  const article9RegistrationDateTime = article9UnixMillisecondsToDateTime(invoice.indati2m)
  const hasProvisionalTaxId = !!invoice.txi?.trim() && !isTaxIdFinal(invoice)
  const logoImage = qrLogoImage()
  let qrText = `${URL}/invoicePrint/${invoice.hsh}`
  const S = (input: string | number | null | undefined) => {
    if (input == null || input == undefined) return ''
    const s = input.toString().trim()
    if (!/^-?\d+(\.\d+)?$/.test(s)) return s // اگر فرمت عددی نبود
    const sign = s.startsWith('-') ? '-' : ''
    let [intPart, frac = ''] = s.replace(/^-/, '').split('.')
    frac = frac.replace(/0+$/, '') // حذف صفرهای انتهایی
    intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',') // 3 رقم 3 رقم
    return sign + intPart + (frac ? '.' + frac : '')
  }
  const selectedType = InvoiceTypeList.find((x) => x.id == invoice.typi)
  var typeName = ''
  var templateName = ''
  if (selectedType) {
    typeName = selectedType.name
    if (selectedType.template) {
      const template = selectedType.template.find((x) => x.id == invoice.tmpi)
      if (template) {
        templateName = template.name
      }
    }
  }
  // Plain text only: older descriptions hold markup, which the server removes from the printed
  // invoice; without its lines the description is read the same way here.
  const descriptionLines =
    noteLines ??
    [invoice.tdsc, invoice.dsc]
      .filter((text): text is string => isNotEmpty(text) && text !== 'null')
      .join('\n')
      .replace(/<\s*br\s*\/?>/gi, '\n')
      .replace(/<[^>]*>/g, '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
  return (
    <>
      <div
        id='posprintplace'
        className='bg-white'
        style={{direction: 'rtl', display: 'block', position: 'relative'}}
        dir='rtl'
      >
        <div className='d-flex flex-column'>
          <div className='w-100 text-center'>
            {onTSP && (
              <>
                {window.location.href.includes('sam') && (
                  <div className='mt-7 fw-bolder fs-2x'>داده کاوی معتمد اول سام</div>
                )}
                {window.location.href.includes('ava') && (
                  <div className='mt-7 fw-bolder fs-2x'>معتمد همراه فن آوا</div>
                )}
                {window.location.href.includes('raham') && (
                  <div className='mt-7 fw-bolder fs-2x'>همیار پایش الکترونیک (رَهام)</div>
                )}
              </>
            )}
            <QRCode
              value={qrText}
              removeQrCodeBehindLogo={true}
              logoPaddingStyle='square'
              // qrStyle="squares"
              logoPadding={2}
              size={220}
              //ecLevel='Q'
              logoImage={logoImage}
            />
          </div>
          <div className='w-100 text-center'>
            <div className='my-2 px-2 fw-bolder fs-2x'>{invoice.sln}</div>
            <div className='my-2 px-2 fw-bolder fs-2x'>{invoice.slc}</div>
            <div className='separator separator-dashed border-gray-800 my-5 mx-3'></div>
            <div className='my-2 px-2 fs-2 fw-bolder fs-3'>{title ?? typeName}</div>
            <div className='my-2 px-2 fs-2 fw-bolder fs-3' dir='rtl' style={{direction: 'rtl'}}>
              {subtitle ?? templateName}
            </div>
            {(isNotEmpty(invoice.buc) || isNotEmpty(invoice.bun)) && (
              <div className='mt-10 px-5 my-5 text-center'>
                <span className='fw-bolder fs-2 d-flex flex-column'>
                  <span className='fs-4 text-gray-800'>خریدار:</span>
                  <span className='fs-2 fw-bolder ms-3'>
                    {invoice.bun} - {invoice.buc}
                  </span>
                </span>
              </div>
            )}
            <div className='mt-10 px-10 d-flex align-items-start'>
              <span className='fw-bolder fs-2'>
                <span className='me-2'>شماره:</span>
                {invoice.no}
              </span>
              <span className='ms-10 flex-grow-1 fw-bolder fs-2 text-end'>
                <span className='d-block fs-4 text-gray-800'>
                  تاریخ و زمان صدور صورتحساب: {invoice.idt}
                </span>
                {isArticle9 && (
                  <span className='d-block fs-4 text-gray-800 mt-2'>
                    <span className='badge badge-light-warning me-2'>ماده ۹</span>
                    تاریخ و زمان ثبت صورتحساب: {article9RegistrationDateTime || '-'}
                  </span>
                )}
              </span>
            </div>
          </div>
          {invoice.itm && invoice.itm.length > 0 && (
            <div className='p-3 w-100 mt-5'>
              {invoice.itm.map((itm, ind) => {
                return (
                  <div className='d-flex w-100 px-3 py-3' key={ind}>
                    <div className='flex-grow-1 d-flex flex-column'>
                      <div className='fw-bolder fs-3 pe-2'>{itm.dsc}</div>
                      <div className='d-flex fs-2 flex-column ps-7 pt-3 fw-bolder'>
                        {itm.prc && itm.prc != '0' && (
                          <span className='me-3'>- قیمت واحد: {S(itm.amt)}</span>
                        )}
                        {itm.qty && itm.qty != '0' && (
                          <span className='me-3'>
                            <span className='me-2'>
                              {invoice.tmpi == 3 ? '- وزن:' : '- تعداد/مقدار:'}
                            </span>{' '}
                            {itm.qty} {itm.unt}
                          </span>
                        )}
                        {itm.dis && itm.dis != '0' && (
                          <span className='me-3'>- تخفیف: {S(itm.dis)}</span>
                        )}
                        {itm.wge && itm.wge != '0' && (
                          <span className='me-3'>- اجرت: {S(itm.wge)}</span>
                        )}
                        {itm.prft && itm.prft != '0' && (
                          <span className='me-3'>- سود: {S(itm.prft)}</span>
                        )}
                        {itm.comm && itm.comm != '0' && (
                          <span className='me-3'>- حق العمل: {S(itm.comm)}</span>
                        )}
                        {isNotEmpty(itm.vtp) && (
                          <span className='me-3'>- مالیات: {S(itm.vtp)}</span>
                        )}
                        {isNotEmpty(itm.cara) && (
                          <span className='me-3'>- عیار: {S(itm.cara)}</span>
                        )}
                      </div>
                    </div>
                    <div className='fw-bolder fs-2'>{S(itm.ntp)}</div>
                  </div>
                )
              })}
            </div>
          )}
          <div className='p-5'>
            <div className='w-100 border border-1 rounded border-dashed border-dark bg-light-primary p-3'>
              <div className='w-100 text-center fs-5'>مجموع صورتحساب:</div>
              <div className='w-100 text-center mt-3'>
                <span className='fw-bolder fs-3x'>
                  {S(invoice.ntp)} <span className='fs-2 text-gray-800 ms-2'>ريال</span>
                </span>
                <div className='fw-bolder fs-3 px-3 mt-3 fw-bolder'>{S(invoice.ntps)}</div>
              </div>
              <div className='d-flex flex-column text-center w-100 px-3 mt-8'>
                <div className='fw-bolder fs-3'>مجموع مالیات بر ارزش افزوده</div>
                <div className='fw-bolder fs-2x'>{S(invoice.vtp)} ریال</div>
              </div>
            </div>
          </div>
          <div className='d-flex w-100 flex-column'>
            <div className='d-flex flex-column w-100 px-3 mt-2'>
              <div className='fs-3 fw-bolder text-center w-100'>شماره منحصر بفرد مالیاتی</div>
              <div className='fw-bolder fs-2x englishText text-center w-100'>
                {invoice.txi}
                {hasProvisionalTaxId && ' *'}
              </div>
              {hasProvisionalTaxId && (
                <div className='fs-7 text-gray-800 text-center w-100 mt-1'>
                  * {PROVISIONAL_TAX_ID_NOTICE}
                </div>
              )}
              {isSandboxInvoice(invoice) && (
                <div className='text-center w-100 mt-2'>
                  <span className='invoice-sandbox-label fs-5'>محیط آزمایشی</span>
                </div>
              )}
            </div>
          </div>
          {invoice.pym && invoice.pym.length > 0 && (
            <>
              <div className='p-3 mt-10'>
                <div className='border border-1 border-solid rounded'>
                  <div className='table-responsive'>
                    <table className='table table-hover table-rounded table-striped border gy-7 gs-7'>
                      <thead>
                        <tr className='fw-bold fs-6 text-gray-800 border-bottom-1 border-gray-400'>
                          <th className='fw-bolder text-center'>تاریخ</th>
                          <th className='fw-bolder text-center'>نحوه پرداخت</th>
                          <th className='fw-bolder text-center'>مبلغ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoice.pym.map((itm, ind) => {
                          const taxValidationError = getPaymentTaxValidationError({
                            ...itm,
                            invoiceNetPrice: invoice.ntp,
                          })
                          return (
                            <Fragment key={itm.id || ind}>
                              <tr className={taxValidationError ? 'table-warning' : ''}>
                                <td className='fw-bolder text-center p-2 fs-3'>{itm.dt}</td>
                                <td className='fw-bolder text-center p-2 fs-3'>
                                  {InvoicePaymentTypeItems.find((x) => x.value == itm.typ)?.label}
                                </td>
                                <td className='fw-bolder text-center p-2 fs-3'>
                                  {taxValidationError && (
                                    <i
                                      className='las la-exclamation-triangle text-warning fs-2 me-2'
                                      title='این ردیف پرداخت سمت سازمان ارسال نخواهد شد'
                                    ></i>
                                  )}
                                  {S(itm.prc)} ریال
                                </td>
                              </tr>
                              {taxValidationError && (
                                <tr className='table-warning'>
                                  <td colSpan={3} className='text-start text-warning fw-bold p-2'>
                                    این ردیف پرداخت سمت سازمان ارسال نخواهد شد: {taxValidationError}
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          )
                        })}
                      </tbody>
                    </table>
                    <div className='p-5'>
                      <div className='w-100 border border-1 rounded border-dashed border-dark bg-light-success p-3'>
                        <div className='w-100 text-center fs-7'>باقیمانده صورتحساب:</div>
                        <div className='w-100 text-center mt-3'>
                          <span className='fw-bolder fs-2x'>
                            {S(invoice.pymr)} <span className='fs-2 text-gray-800 ms-2'>ريال</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
          {descriptionLines.length > 0 && (
            <div className='p-3'>
              <h5>توضیحات:</h5>
              {descriptionLines.map((line, index) => (
                <p key={index} className='px-3 mb-1' style={{textAlign: 'justify'}}>
                  {line || '\u00a0'}
                </p>
              ))}
            </div>
          )}
        </div>
        <div className='text-white w-100 p-5'>.</div>
      </div>
    </>
  )
}
export {InvoicePrintPOS}
